import { useContext, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { AssignmentResponse, ClassResponse, StudentResponse } from "shared/types/teacherTypes"
import PreviewReviewAssignment from "./PreviewReviewAssignment"
import { editAssignment, findAssignmentById, getStudents } from "shared/routes/teacher"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import { Link } from "react-router-dom"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import moment from "moment"
import { AssessmentViewOptions, AssessmentViewOptionsType } from "shared/types/assessmentTypes"
import * as Yup from "yup"
import { Field, FormikProvider, useFormik } from "formik"
import AssignOptionToggle from "../Forms/AssignmentForm/BulkComponents/AssignOptionToggle"
import SelectNoneAll from "../Forms/AssignmentForm/BulkComponents/SelectNoneAll"
import SelectStudents from "../Forms/AssignmentForm/SelectStudents"
import { BlockType, EnableReadingLevelBlocks } from "shared/types/science35Types"
import { toProductString } from "helpers/blockStringConverter"
import { AuthContext } from "AuthContext"

const EditReviewAssignment = ( { activeClass }: { activeClass: ClassResponse }) => {
    
    const location = useLocation()
    const state = location.state as { assignmentId: string, blockType: BlockType }

    const navigate = useNavigate()
    const [assignment, setSelectedAssignment] = useState<AssignmentResponse | null>(null)
    const [students, setStudents] = useState<StudentResponse[]>(null)

    const [modalView, setModalView] = useState<"students" | null>(null)

    useEffect(() => {
        if (!state?.assignmentId) return
        void findAssignmentById(state.assignmentId).then((a) => {
                setSelectedAssignment(a)
            }).catch((err) => {
                toast.error(err || "There was an error loading the assignment. Please try to reselect the assignment.")
            })
    }, [state?.assignmentId])

    useEffect(() => {
        if (activeClass?.id) {
            getStudents({ classID: activeClass.id }).then((response) => {
                setStudents(response)
            })
        } else {
            setStudents(null)
        }
    }, [activeClass?.id])

    const updateAssignment = () => {
        editAssignment({
            id: assignment.id,
            title: assignment.title,
            classID: activeClass.id,
            dueDate: formik.values.dueDate ? moment(formik.values.dueDate).utc().toDate() : undefined,
            lessonVariety: assignment.lessonVariety,
            reviewId: assignment.review?.id,
            studentAssessmentViewOption: formik.values.studentAssessmentViewOption,
            oneQuestionMode: formik.values.oneQuestionMode,
            randomizable: formik.values.randomizable,
            useGrade3: formik.values.useGrade3,
            students: formik.values.students,
        })
        .then(() => {
            toast.success("Successfully updated assignment.")
            navigate(`/dashboard/teacher/science/${toProductString(state?.blockType)}/assignments`)
        })
        .catch(() => toast.error("There was an error updating the assignment. Please try again."))
    }

    const { scienceBlocks } = useContext(AuthContext)
    const readingLevelEnabled = scienceBlocks.some(a => EnableReadingLevelBlocks.includes(a)) && !!(assignment?.review?.grade3pretestId || assignment?.review?.grade3lessonId || assignment?.review?.grade3postTestId)

    const formik = useFormik({
            initialValues: {
                dueDate: assignment ? (assignment.dueDate ? moment(assignment.dueDate).format("YYYY-MM-DD") : undefined) : undefined,
                studentAssessmentViewOption: assignment ? assignment.studentAssessmentViewOption : activeClass.studentAssessmentViewOption,
                oneQuestionMode: assignment ? assignment.oneQuestionMode : true,
                randomizable: assignment ? assignment.randomizable : false,
                students: assignment ? assignment.students ?? [] : [],
                useGrade3: assignment ? assignment.useGrade3 : false,
            },
            onSubmit: null,
            enableReinitialize: true,
            validationSchema: Yup
                .object()
                .shape({
                    dueDate: Yup
                        .date()
                        .label("Due Date")
                        .nullable()
                        .notRequired(),
                    studentAssessmentViewOption: Yup
                        .string(),
                    oneQuestionMode: Yup
                        .boolean(),
                    randomizable: Yup
                        .boolean(),
                    students: Yup
                        .array()
                        .nullable()
                        .optional(),
                    useGrade3: Yup
                        .boolean(),
                })
        })

    if (!assignment) return <BeatLoader />
  return (
    <div>
        <div className="d-flex justify-content-between align-items-center m-2">
            <Link style={{
                "textDecoration": "none"
            }} to={`/dashboard/teacher/science/${toProductString(state?.blockType)}/assignments`}>
                <i className="fas fa-arrow-left" /> Return to Assignments</Link>
        </div>
        <Row>
            <Col xs={{ order: 2, span: 12  }} md={{ order: 1, span: 9 }}>
                <PreviewReviewAssignment assignment={assignment} />
            </Col>
            <Col xs={{ order: 1, span: 12  }} md={{ order: 2, span: 3 }} className="border border-1 rounded my-4 my-md-0">
                <FormikProvider value={formik}>
                    <Button variant="primary" disabled={!formik.dirty || formik.isSubmitting} onClick={updateAssignment} className="w-100 my-2">Update Assignment</Button>
                    <Form.Group className="mb-3 text-start">
                        <Form.Label>Student Results View</Form.Label>
                        <Form.Select
                            value={formik.values.studentAssessmentViewOption}
                            className="mb-3"
                            name="studentAssessmentViewOption"
                            onChange={e => formik.setFieldValue("studentAssessmentViewOption", e.target.value as AssessmentViewOptionsType)}>
                            {AssessmentViewOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3 text-start">
                        <Form.Label>Questions Per Page</Form.Label>
                        <Form.Select
                            name="oneQuestionMode"
                            value={formik.values.oneQuestionMode ? "true" : "false"}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    e.stopPropagation()
                                    formik.setFieldValue("oneQuestionMode", e.target.value === "true")
                                }
                            }
                        >
                            <option value="true">One</option>
                            <option value="false">Multiple</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3 text-start">
                        <Form.Label>Randomize Assessment Answers?</Form.Label>
                        <Form.Select
                            name="randomizable"
                            value={formik.values.randomizable ? "true" : "false"}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    e.stopPropagation()
                                    formik.setFieldValue("randomizable", e.target.value === "true")
                                }
                            }
                        >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </Form.Select>
                    </Form.Group>
                    {readingLevelEnabled &&
                        <Form.Group className="mb-3 text-start">
                            <Form.Label className="mb-0">Reading Level</Form.Label>
                            <Form.Label className="fw-light text-muted">Adjusts text complexity for selected assignments</Form.Label>
                            <Form.Select
                                name="useGrade3"
                                value={formik.values.useGrade3 ? "true" : "false"}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    e.stopPropagation()
                                    formik.setFieldValue("useGrade3", e.target.value === "true")
                                }}
                            >
                                <option value="true">Grade 3</option>
                                <option value="false">Grade 5</option>
                            </Form.Select>
                        </Form.Group>
                    }
                    <Form.Group className="mb-3 text-start">
                        <Form.Label>Assign To</Form.Label>
                        <AssignOptionToggle 
                            onClassChosen={() => formik.setFieldValue("students", null)}
                            onStudentsChosen={() => {
                                if (!formik.values.students) {
                                    formik.setFieldValue("students", [])
                                }
                                setModalView("students")
                            }}
                            assignOption={formik.values.students?.length > 0 ? "students" : "class"}
                        />
                         {formik.values.students?.map((studentId) => {
                            const student = students?.find(student => student.uuid === studentId)
                            return <div className="text-start mb-1 d-flex justify-content-between">
                                <span style={{overflowInline: "hidden", whiteSpace: "nowrap"}}>{student?.name}</span>
                                <i
                                    className="text-danger fas fa-times-circle my-auto ms-1"
                                    style={{cursor: "pointer"}}
                                    onClick={() => {
                                        const newStudents = formik.values.students.filter((value) => value !== student.uuid)
                                        formik.setFieldValue("students", newStudents)
                                    }}
                                />
                            </div>
                        })}
                    </Form.Group>
                    <Form.Group className="mb-3 text-start">
                        <Form.Label>Due Date</Form.Label>
                        <Form.Control
                            as={Field}
                            type="date"
                            name="dueDate"
                            max="9999-12-31"
                            isInvalid={formik.values.dueDate && !!formik.errors.dueDate}
                            disabled={formik.isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid" className="text-center">
                            {formik.errors.dueDate}
                        </Form.Control.Feedback>
                    </Form.Group>
                </FormikProvider>
            </Col>
        </Row>
        {formik.values.students &&
            <Modal show={modalView === "students"} onHide={() => setModalView(null)}>
                <Modal.Header className="border-0" closeButton><h1 className="h3 my-auto">Select Students to Assign</h1></Modal.Header>
                <Modal.Body>
                    <SelectStudents 
                        classStudents={students}
                        selectedStudentIds={formik.values.students}
                        onSelectStudent={newStudents => formik.setFieldValue("students", newStudents)}
                        onUnselectStudent={newStudents => formik.setFieldValue("students", newStudents)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-center w-100">
                        <SelectNoneAll
                            selectNone={() => {
                                formik.setFieldValue("students", [])
                            }} 
                            selectAll={() => {
                                const newStudents = students.map(student => student.uuid)
                                formik.setFieldValue("students", newStudents)
                            }} />
                        <div>
                            <Button variant="theme" className="float-end" onClick={() => setModalView(null)}>Save</Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        }
    </div>
  )
}

export default EditReviewAssignment
